import { useMemo, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type ColumnFiltersState,
    type SortingState,
} from '@tanstack/react-table';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Bot, User } from 'lucide-react';
import { Button, EmptyState, ErrorState } from '@fiado/react-kit';
import { resolveErrorMessage } from '../../api/errorMapping';
import { Gate } from '../permissions/Gate';
import { StatusBadge } from '../shared/StatusBadge';
import { USER_STATUS_META, USER_STATUS_OPTIONS, type UserStatus } from '../../lib/userStatus';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ContentLoading } from '../shared/ContentLoading';
import { DataTableToolbar } from '../shared/DataTableToolbar';
import { DataTablePagination } from '../shared/DataTablePagination';
import { SortableHeader } from '../shared/SortableHeader';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { ariaSortOf, globalFilterFn } from '../../lib/dataTable';
import { SELECT_CLASS } from '../../lib/selectClass';
import { buildIdentifierColumn } from './identifierColumn';
import { DisplayNameCell } from './DisplayNameCell';
import { buildLevelColumn, levelFilterOptions, NO_LEVEL_VALUE } from './levelColumns';
import { ScopedUserForm } from './ScopedUserForm';
import { SegmentedControl } from '../shared/SegmentedControl';
import type { ScopeDescriptor } from './scopeDescriptors';
import { userPerm } from './scopeDescriptors';
import type {
    TenantUserListItemLocal,
    TenantUserListResponseLocal,
} from '../../api/types/TenantLocal';

// Columnas base comunes a ambos scopes (sin acciones ni createdAt). La columna de acciones y la de
// createdAt se arman dentro del componente porque dependen del descriptor (scopeRef / showCreatedAt).
// El tipo del item es común: RetailerUserListItemLocal === TenantUserListItemLocal.
// DEC-RBAC-079/081: la columna del identificador NO va aquí (module-level) porque depende del
// `principalIdentifier` del tenant (dato async) — se inserta en el useMemo del componente vía
// `buildIdentifierColumn`, entre Nombre y Estado.
const baseColumns: ColumnDef<TenantUserListItemLocal>[] = [
    {
        accessorKey: 'displayName',
        header: ({ column }) => <SortableHeader column={column} title="Nombre" />,
        cell: (info) => <DisplayNameCell user={info.row.original} />,
    },
    {
        accessorKey: 'status',
        header: ({ column }) => <SortableHeader column={column} title="Estado" />,
        // El texto del badge alimenta el global search; el filterValue es el código del estado.
        filterFn: 'equals',
        cell: (info) => <StatusBadge status={info.getValue() as UserStatus | undefined} />,
    },
    {
        // Render de roles unificado en badges (versión tenant) para ambos scopes.
        accessorKey: 'roles',
        header: () => (
            <span className="inline-flex items-center justify-center gap-1">
                Roles
                <HelpHint text={HELP_TEXT.role} side="right" />
            </span>
        ),
        enableSorting: false,
        cell: (info) => {
            const roles = info.getValue() as string[] | undefined;
            if (!roles || roles.length === 0) {
                return <span className="text-muted-foreground">Sin roles</span>;
            }
            return (
                <div className="flex flex-wrap justify-center gap-1">
                    {roles.map((r) => (
                        <span
                            key={r}
                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
                        >
                            {r}
                        </span>
                    ))}
                </div>
            );
        },
    },
];

/** Filtro rápido por tipo de usuario: '' = todos. */
type AccountTypeFilter = '' | 'HUMAN' | 'API';

const ACCOUNT_TYPE_FILTER_LABELS: Record<AccountTypeFilter, string> = {
    '': 'Todos',
    HUMAN: 'Personas',
    API: 'Sistemas',
};

interface Props {
    descriptor: ScopeDescriptor;
    /**
     * scopeRef explícito que tiene prioridad sobre `descriptor.useScopeRef()` (mismo patrón que
     * `ScopedAuditTable`). Lo usa el drill-down platform: el tenantId sale del PATH, no del actor.
     */
    scopeRef?: string;
}

/**
 * Tabla de usuarios de un tenant. Todo el comportamiento (filtros, status, modal crear, paginación,
 * "cargar más") se parametriza por el descriptor: API/scopeRef, queryKey, permiso del botón crear,
 * el form de crear, el identificador y las row-actions. Sirve al flujo tenant propio (scopeRef del
 * actor) y al drill-down platform (scopeRef del path por prop) con el MISMO motor.
 */
export function ScopedUsersTable({ descriptor, scopeRef: scopeRefProp }: Props) {
    const descriptorScopeRef = descriptor.useScopeRef();
    const scopeRef = scopeRefProp ?? descriptorScopeRef;
    // DEC-RBAC-079/081: identificador principal del tenant del `scopeRef` (propio o del path según el
    // descriptor). Default EMAIL para tenants viejos / mientras carga.
    const { principalIdentifier } = descriptor.useIdentifier(scopeRef ?? '');
    // Niveles de la aplicación del tenant → una columna (+ su filtro) por nivel, rotulada con el
    // nombre real que le puso el tenant. `[]` mientras carga o si no tiene niveles → no se pintan.
    const { levels } = descriptor.useLevels(scopeRef ?? '');
    // Encabezados con el nombre del nivel según el resolvedor del tenant visto.
    const labels = descriptor.useLabels(scopeRef ?? '');
    const namedLevels = useMemo(
        () => levels.map((lvl) => ({ level: lvl.level, displayName: labels.scope(lvl.level) })),
        [levels, labels],
    );
    const [createOpen, setCreateOpen] = useState(false);
    // Estado client-side de la tabla (search / filtro estado / filtros por nivel / orden / paginación).
    const [globalFilter, setGlobalFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [accountTypeFilter, setAccountTypeFilter] = useState<AccountTypeFilter>('');
    // Filtro por nivel: `level` → scopeRef elegido (o el sentinel NO_LEVEL_VALUE). '' = sin filtrar.
    const [levelFilters, setLevelFilters] = useState<Record<string, string>>({});
    const [sorting, setSorting] = useState<SortingState>([{ id: 'displayName', desc: false }]);
    const { data, fetchNextPage, hasNextPage, isFetching, isLoading, isError, error, refetch } =
        useInfiniteQuery({
            queryKey: [...descriptor.queryKeyBase(scopeRef ?? ''), 'users'],
            queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
                descriptor.api.listUsers(scopeRef ?? '', {
                    cursor: pageParam,
                    pageSize: 25,
                }) as Promise<TenantUserListResponseLocal>,
            initialPageParam: undefined as string | undefined,
            getNextPageParam: (last) => last.lastEvaluatedKey,
            enabled: !!scopeRef,
        });

    // Columna de acciones: el menú "⋮" por fila. El wrapper se elige por scope (ambos ya unificados
    // sobre shared/UserActionsMenu), pasando el scopeRef que cada uno espera.
    // useMemo OBLIGATORIO (igual que `rows`): columns con referencia estable o useReactTable entra en
    // re-render infinito. `descriptor` + `scopeRef` son las dependencias reales.
    const columns = useMemo<ColumnDef<TenantUserListItemLocal>[]>(
        () => [
            baseColumns[0], // Nombre
            buildIdentifierColumn(principalIdentifier), // Identificador principal del tenant
            ...baseColumns.slice(1), // Estado, Roles
            // Una columna por nivel del tenant (Grupos / Sucursales / …), antes de las acciones.
            ...namedLevels.map(buildLevelColumn),
            {
                id: 'actions',
                header: 'Acciones',
                enableSorting: false,
                enableGlobalFilter: false,
                cell: ({ row }) =>
                    descriptor.renderRowActions({ user: row.original, scopeRef: scopeRef ?? '' }),
            },
        ],
        [descriptor, scopeRef, principalIdentifier, namedLevels],
    );

    const columnFilters: ColumnFiltersState = useMemo(
        () => [
            ...(statusFilter ? [{ id: 'status', value: statusFilter }] : []),
            // Un filtro activo por cada nivel con selección (se combinan en AND, como espera el usuario).
            ...Object.entries(levelFilters)
                .filter(([, value]) => !!value)
                .map(([level, value]) => ({ id: `level:${level}`, value })),
        ],
        [statusFilter, levelFilters],
    );

    // useMemo OBLIGATORIO: useReactTable compara `data` por referencia. Sin memo, flatMap crea un
    // array nuevo cada render → Table cree que cambió → re-render → array nuevo → LOOP INFINITO
    // (la página se congela con el CPU al 100%). Footgun documentado de TanStack Table.
    // El orden lo maneja getSortedRowModel (sorting state, default por displayName); aquí solo
    // aplanamos las páginas del server con referencia estable.
    const rows = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
    const accountTypeOptions = useMemo(() => {
        const apiCount = rows.filter((r) => r.accountType === 'API').length;
        const counts: Record<AccountTypeFilter, number> = {
            '': rows.length,
            HUMAN: rows.length - apiCount,
            API: apiCount,
        };
        return (['', 'HUMAN', 'API'] as const).map((value) => ({
            value,
            label: `${ACCOUNT_TYPE_FILTER_LABELS[value]} (${String(counts[value])})`,
            Icon: value === 'API' ? Bot : value === 'HUMAN' ? User : undefined,
        }));
    }, [rows]);
    const tableRows = useMemo(
        () =>
            accountTypeFilter
                ? rows.filter((r) => (r.accountType ?? 'HUMAN') === accountTypeFilter)
                : rows,
        [rows, accountTypeFilter],
    );
    const table = useReactTable({
        data: tableRows,
        columns,
        state: { globalFilter, columnFilters, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 } },
    });

    // Spinner del área de contenido mientras la primera página carga. `isLoading` (no `isPending`)
    // porque la query es condicional (`enabled: !!scopeRef`): sin scopeRef no se cuelga.
    if (isLoading) return <ContentLoading />;
    if (isError) {
        return (
            <ErrorState
                title="No pudimos cargar los usuarios"
                message={resolveErrorMessage(error)}
                retryLabel="Reintentar"
                onRetry={() => {
                    void refetch();
                }}
            />
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate
                    permission={userPerm(descriptor, 'create')}
                    scope={descriptor.scope}
                    scopeRef={scopeRef}
                >
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear usuario
                    </Button>
                </Gate>
            </div>

            {/* Modal plano: overlay + caja. Backdrop = <button> nativo (accesible) detrás de la caja
                → click afuera cierra sin onClick en divs. (Radix Dialog también funcionaría; el
                freeze de esta pantalla era el re-render loop de useReactTable de arriba.) */}
            {createOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCreateOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Crear usuario</h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCreateOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <ScopedUserForm
                            descriptor={descriptor}
                            scopeRef={scopeRef}
                            onDone={() => {
                                setCreateOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}

            <SegmentedControl
                legend="Tipo de usuario"
                hideLegend
                name="users-account-type"
                value={accountTypeFilter}
                options={accountTypeOptions}
                onChange={setAccountTypeFilter}
            />

            <DataTableToolbar value={globalFilter} onChange={setGlobalFilter}>
                <select
                    aria-label="Estado"
                    className={`${SELECT_CLASS} w-auto`}
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                    }}
                >
                    <option value="">Todos</option>
                    {USER_STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                            {USER_STATUS_META[s].label}
                        </option>
                    ))}
                </select>
                {/* Un filtro por nivel. Las opciones salen de las filas cargadas (el list trae todos
                    los usuarios de una), así no se ofrece un destino que daría la tabla vacía. */}
                {namedLevels.map((lvl) => {
                    const options = levelFilterOptions(rows, lvl.level);
                    if (options.length === 0) return null;
                    return (
                        <select
                            key={lvl.level}
                            aria-label={lvl.displayName}
                            className={`${SELECT_CLASS} w-auto`}
                            value={levelFilters[lvl.level] ?? ''}
                            onChange={(e) => {
                                setLevelFilters((prev) => ({
                                    ...prev,
                                    [lvl.level]: e.target.value,
                                }));
                            }}
                        >
                            <option value="">{lvl.displayName}: todos</option>
                            {options.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                            <option value={NO_LEVEL_VALUE}>Sin asignar</option>
                        </select>
                    );
                })}
            </DataTableToolbar>

            {rows.length === 0 ? (
                <EmptyState
                    title="Todavía no hay usuarios"
                    description="Cuando des de alta un usuario, aparecerá aquí."
                />
            ) : (
                <>
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((hg) => (
                                <TableRow key={hg.id}>
                                    {hg.headers.map((h) => (
                                        <TableHead
                                            key={h.id}
                                            className="text-center"
                                            aria-sort={ariaSortOf(h.column)}
                                        >
                                            {flexRender(h.column.columnDef.header, h.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.map((r) => (
                                <TableRow
                                    key={r.id}
                                    className={
                                        (r.original as TenantUserListItemLocal).accountType ===
                                        'API'
                                            ? 'bg-info/5'
                                            : undefined
                                    }
                                >
                                    {r.getVisibleCells().map((c) => (
                                        <TableCell key={c.id} className="text-center align-middle">
                                            {flexRender(c.column.columnDef.cell, c.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <DataTablePagination table={table} />
                </>
            )}

            {/* "Cargar más" trae MÁS filas del server (cursor); la paginación numérica de arriba es
                sobre lo ya cargado. Las nuevas filas entran al modelo filtrado/paginado/ordenado. */}
            {hasNextPage && (
                <Button
                    variant="outline"
                    onClick={() => {
                        void fetchNextPage();
                    }}
                    disabled={isFetching}
                >
                    Cargar más
                </Button>
            )}
        </div>
    );
}
